import { createClient } from '@supabase/supabase-js';
import { createCrossSubdomainAuthStorage } from '@rehub/supabase/auth-storage';
import type { PlanosProject, Hotspot, LatLng, UserProfile, ViewMode } from '../types';

// Explicitly access environment variables to ensure Vite's static replacement works correctly.
const supabaseUrl = (import.meta as any).env?.VITE_SUPABASE_URL || process.env.VITE_SUPABASE_URL || '';
const supabaseAnonKey = (import.meta as any).env?.VITE_SUPABASE_ANON_KEY || process.env.VITE_SUPABASE_ANON_KEY || '';

export const isConfigured = !!(supabaseUrl && supabaseAnonKey && supabaseUrl.startsWith('http') && !supabaseUrl.includes('placeholder'));
export { supabaseUrl };

if (!isConfigured) {
  console.warn('Planos: Supabase env missing. Set VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY.');
}

const validUrl = isConfigured ? supabaseUrl : 'https://placeholder.supabase.co';
const validKey = isConfigured ? supabaseAnonKey : 'placeholder';

export const supabase = createClient(validUrl, validKey, {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: true,
    flowType: 'pkce',
    storage: createCrossSubdomainAuthStorage(),
  },
  global: {
    headers: {},
  },
});

export { isConfigured as configured };

// Stripe billing portal (invoices, payment methods, subscriptions). Shared
// across every ReHub app via @rehub/commerce + the single Supabase project.
import { openBillingPortal as openBillingPortalShared } from '@rehub/commerce';
export const openBillingPortal = (returnUrl?: string) =>
  openBillingPortalShared(supabase, returnUrl);

/* ─── Profile / credits ─────────────────────────────────── */

export const getProfile = async (userId: string): Promise<UserProfile | null> => {
  if (!isConfigured) return null;
  const { data, error } = await supabase.from('profiles').select('id, email, credits').eq('id', userId).single();
  if (error || !data) {
    console.warn('[Planos] getProfile failed:', error?.message || 'no data');
    return null;
  }
  return data as UserProfile;
};

export const consumeCredit = async (userId: string): Promise<'success' | 'insufficient_credits' | 'error'> => {
  try {
    const { error } = await supabase.rpc('consume_credit', { user_id: userId });
    if (error) {
      if (error.message.includes('INSUFFICIENT_CREDITS')) return 'insufficient_credits';
      console.error('[Planos] consume_credit error:', error);
      return 'error';
    }
    return 'success';
  } catch (err) {
    console.error('[Planos] consume_credit exception:', err);
    return 'error';
  }
};

export const refundCredit = async (userId: string): Promise<void> => {
  try {
    await supabase.rpc('refund_credit', { user_id: userId });
  } catch (err) {
    console.error('[Planos] refund_credit error:', err);
  }
};

/* ─── Project CRUD ───────────────────────────────────────── */

export const getProjects = async (userId: string): Promise<PlanosProject[]> => {
  if (!isConfigured) return [];
  const { data, error } = await supabase
    .from('planos_projects')
    .select('*')
    .eq('user_id', userId)
    .order('updated_at', { ascending: false });
  if (error) {
    console.error('[Planos] getProjects error:', error.message);
    return [];
  }
  return (data ?? []) as PlanosProject[];
};

export const getProjectByIdPublic = async (projectId: string): Promise<PlanosProject | null> => {
  if (!isConfigured) return null;
  const { data, error } = await supabase
    .from('planos_projects')
    .select('*')
    .eq('id', projectId)
    .single();
  if (error || !data) return null;
  const p = data as PlanosProject;
  if (p.hosting_status !== 'active' && !p.hosting_permanent) return null;
  return p;
};

export const getProjectBySlugPublic = async (slug: string): Promise<PlanosProject | null> => {
  if (!isConfigured) return null;
  const { data, error } = await supabase
    .from('planos_projects')
    .select('*')
    .eq('slug', slug)
    .single();
  if (error || !data) return null;
  const p = data as PlanosProject;
  if (p.hosting_status !== 'active' && !p.hosting_permanent) return null;
  return p;
};

/** Fetch by slug or UUID (for backward compat with /view/:id) */
export const getProjectPublic = async (slugOrId: string): Promise<PlanosProject | null> => {
  const isUuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(slugOrId);
  if (isUuid) return getProjectByIdPublic(slugOrId);
  return getProjectBySlugPublic(slugOrId);
};

function generateSlug(): string {
  return 'p-' + crypto.randomUUID().replace(/-/g, '').slice(0, 8);
}

export const createProject = async (userId: string, title?: string, viewMode: ViewMode = 'map'): Promise<PlanosProject | null> => {
  if (!isConfigured) return null;
  const slug = generateSlug();
  const { data, error } = await supabase
    .from('planos_projects')
    .insert({
      user_id: userId,
      title: title ?? 'Untitled Plan',
      slug,
      google_coords: { lat: 51.5074, lng: -0.1278 },
      hotspots: [],
      view_mode: viewMode,
    })
    .select()
    .single();
  if (error) {
    console.error('[Planos] createProject error:', error.message);
    return null;
  }
  return data as PlanosProject;
};

export type UpdateProjectResult = { data: PlanosProject; error: null } | { data: null; error: string };

export const updateProject = async (
  projectId: string,
  updates: Partial<Pick<PlanosProject, 'title' | 'internal_job_number' | 'address' | 'google_coords' | 'floorplan_image_url' | 'overlay_bounds' | 'map_heading' | 'hotspots' | 'password' | 'slug' | 'view_mode' | 'viewer_settings' | 'floors'>>
): Promise<UpdateProjectResult> => {
  if (!isConfigured) return { data: null, error: 'Not configured' };
  // Only send defined values so we never accidentally clear columns with undefined
  const payload: Record<string, unknown> = { updated_at: new Date().toISOString() };
  for (const [key, value] of Object.entries(updates)) {
    if (value === undefined) continue;
    if (key === 'floors' && Array.isArray(value)) {
      payload[key] = JSON.parse(JSON.stringify(value));
    } else {
      payload[key] = value;
    }
  }
  const { data, error } = await supabase
    .from('planos_projects')
    .update(payload)
    .eq('id', projectId)
    .select()
    .single();
  if (error) {
    console.error('[Planos] updateProject error:', error.message);
    return { data: null, error: error.message };
  }
  return { data: data as PlanosProject, error: null };
};

export const deleteProject = async (projectId: string): Promise<boolean> => {
  if (!isConfigured) return false;
  const { error } = await supabase.from('planos_projects').delete().eq('id', projectId);
  if (error) {
    console.error('[Planos] deleteProject error:', error.message);
    return false;
  }
  return true;
};

/** @deprecated Use HostingModal + activatePlanosHosting instead */
export const publishProject = async (projectId: string, userId: string): Promise<'success' | 'insufficient_credits' | 'error'> => {
  const creditResult = await consumeCredit(userId);
  if (creditResult !== 'success') return creditResult;

  const { error } = await supabase
    .from('planos_projects')
    .update({ is_published: true, credits_spent: 1, updated_at: new Date().toISOString() })
    .eq('id', projectId);

  if (error) {
    console.error('[Planos] publishProject error:', error.message);
    await refundCredit(userId);
    return 'error';
  }
  return 'success';
};

/* ─── Storage ────────────────────────────────────────────── */

function base64ToBlob(base64: string): Blob {
  const parts = base64.split(';base64,');
  const contentType = parts[0].split(':')[1];
  const raw = window.atob(parts[1]);
  const uInt8Array = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; ++i) uInt8Array[i] = raw.charCodeAt(i);
  return new Blob([uInt8Array], { type: contentType });
}

export const uploadImage = async (userId: string, imageData: File | Blob | string, folder = 'planos'): Promise<string | null> => {
  if (!isConfigured) return null;
  try {
    let blob: Blob;
    let ext = 'jpg';
    if (imageData instanceof File) {
      blob = imageData;
      ext = imageData.name.split('.').pop() || 'jpg';
    } else if (typeof imageData === 'string') {
      blob = base64ToBlob(imageData);
      if (imageData.startsWith('data:image/png')) ext = 'png';
      else if (imageData.startsWith('data:image/webp')) ext = 'webp';
    } else {
      blob = imageData;
    }
    const filename = `${userId}/${folder}/${crypto.randomUUID()}.${ext}`;
    const { error } = await supabase.storage.from('images').upload(filename, blob, { cacheControl: '3600', upsert: true });
    if (error) throw error;
    const { data: { publicUrl } } = supabase.storage.from('images').getPublicUrl(filename);
    return publicUrl;
  } catch (err) {
    console.error('[Planos] uploadImage error:', err);
    return null;
  }
};

/** Upload a PDF or other file for hosting (e.g. brochures). Returns public URL or null. */
export const uploadFile = async (userId: string, file: File, folder = 'planos-downloads'): Promise<string | null> => {
  if (!isConfigured) return null;
  try {
    const ext = file.name.split('.').pop() || 'pdf';
    const filename = `${userId}/${folder}/${crypto.randomUUID()}.${ext}`;
    const { error } = await supabase.storage.from('images').upload(filename, file, {
      cacheControl: '3600',
      upsert: true,
      contentType: file.type || (ext === 'pdf' ? 'application/pdf' : undefined),
    });
    if (error) throw error;
    const { data: { publicUrl } } = supabase.storage.from('images').getPublicUrl(filename);
    return publicUrl;
  } catch (err) {
    console.error('[Planos] uploadFile error:', err);
    return null;
  }
};
